<script setup lang="ts">
import { computed, ref } from "vue"
import { Check, ChevronsUpDown } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type IngredientOption = {
    id: number
    name: string
    unit: string
    unit_cost_in_cents: number
}

const props = withDefaults(
    defineProps<{
        modelValue: string
        options: IngredientOption[]
        ingredients?: IngredientOption[]
        placeholder?: string
        invalid?: boolean
    }>(),
    {
        placeholder: "Select ingredient",
        invalid: false,
    }
)

const emit = defineEmits<{
    "update:modelValue": [value: string]
}>()

const open = ref(false)

const displayLabel = computed(() => {
    if (!props.modelValue) {
        return props.placeholder
    }

    const catalog = props.ingredients ?? props.options
    return catalog.find((item) => String(item.id) === props.modelValue)?.name ?? props.placeholder
})

function selectIngredient(ingredient: IngredientOption) {
    emit("update:modelValue", String(ingredient.id))
    open.value = false
}
</script>

<template>
    <Popover v-model:open="open">
        <PopoverTrigger as-child>
            <Button
                type="button"
                variant="outline"
                role="combobox"
                :aria-expanded="open"
                :class="
                    cn(
                        'h-10 w-full justify-between font-normal',
                        !modelValue && 'text-muted-foreground',
                        invalid && 'border-destructive ring-2 ring-destructive'
                    )
                "
            >
                <span class="truncate">{{ displayLabel }}</span>
                <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
        </PopoverTrigger>
        <PopoverContent
            class="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            @close-auto-focus.prevent
        >
            <Command>
                <CommandInput placeholder="Search ingredients..." />
                <CommandList class="max-h-72">
                    <CommandEmpty>No ingredient found.</CommandEmpty>
                    <CommandGroup>
                        <CommandItem
                            v-for="ingredient in options"
                            :key="ingredient.id"
                            :value="ingredient.name"
                            @select="selectIngredient(ingredient)"
                        >
                            <Check
                                :class="
                                    cn(
                                        'mr-2 h-4 w-4',
                                        modelValue === String(ingredient.id) ? 'opacity-100' : 'opacity-0'
                                    )
                                "
                            />
                            {{ ingredient.name }}
                        </CommandItem>
                    </CommandGroup>
                </CommandList>
            </Command>
        </PopoverContent>
    </Popover>
</template>
